// screen: Accept invitation / set password
import * as React from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useLingui } from '@lingui/react/macro';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError } from '@viewstream/api-client';
import { Button, EmptyState, Field, Input, Skeleton } from '@viewstream/ui';
import { useAuth } from '~/app/auth';
import { api } from '~/app/api';
import { ProductName } from '~/app/brand';

export const Route = createFileRoute('/invite/$token')({ component: Invite });

function Invite() {
  const { t } = useLingui();
  const { token } = Route.useParams();
  const navigate = useNavigate();
  const { login, logout, me } = useAuth();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['invitation', token], queryFn: async () => (await api.GET('/v1/invitations/{token}', { params: { path: { token } } })).data, retry: false });
  const [password, setPassword] = React.useState('');
  const [name, setName] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState<string | null>(null);
  const signedInAs = me?.user?.email.toLowerCase();
  async function accept(e: React.FormEvent) {
    e.preventDefault(); setBusy(true); setErr(null);
    const inv = q.data!;
    try {
      await api.POST('/v1/invitations/{token}/accept', { params: { path: { token } }, body: { password: inv.existing_user ? undefined : password, name: name || undefined } });
      if (inv.existing_user) {
        // the membership was added to the signed-in account: refresh its tenants and go there
        await qc.invalidateQueries();
        void navigate({ to: '/t/$slug', params: { slug: inv.tenant.slug } });
        return;
      }
      // a new account: sign in with the password just chosen, straight into the tenant
      await login(inv.email, password);
      void navigate({ to: '/t/$slug', params: { slug: inv.tenant.slug } });
    } catch (ex) {
      const errs = ex instanceof ApiError ? ex.problem?.errors : undefined;
      setErr(errs?.length ? errs.map((f) => f.detail).join('; ') : ex instanceof ApiError ? (ex.problem?.detail ?? ex.problem?.title ?? ex.message) : String(ex));
    } finally { setBusy(false); }
  }
  const gone = (e: unknown) => {
    const x = e as ApiError;
    if (x.status === 410) return x.problem?.detail === 'invitation was revoked' ? t`This invitation was revoked. Ask your administrator for a new one.`
      : x.problem?.detail === 'invitation already accepted' ? t`This invitation was already accepted. Sign in with your account.` : t`This invitation has expired. Ask your administrator to send it again.`;
    if (x.isNotFound) return t`This link is invalid or was replaced by a newer invitation mail.`;
    return x.message;
  };
  return (
    <main className="flex min-h-dvh items-center justify-center p-6">
      <div className="w-full max-w-sm rounded-xl border border-border bg-surface p-8 shadow-card">
        <div className="mb-6"><ProductName /></div>
        {q.isPending ? <Skeleton className="h-24 w-full" /> : q.isError ? (
          <EmptyState title={t`Invitation not valid`} description={gone(q.error)}
            actions={<Button variant="secondary" onClick={() => navigate({ to: '/login', search: { next: undefined } })}>{t`Sign in`}</Button>} />
        ) : (
          <form onSubmit={accept} noValidate>
            <h1 className="mb-1 text-xl font-semibold">{t`Join ${q.data!.tenant.name}`}</h1>
            <p className="mb-4 text-sm text-fg-muted">{t`as ${q.data!.role} · ${q.data!.email}`}</p>
            {/* security audit 2026-10-06 H1: an existing account accepts only while signed in as the invited e-mail */}
            {q.data!.existing_user && signedInAs !== q.data!.email.toLowerCase() ? (
              signedInAs ? (
                <>
                  <p className="mb-4 text-sm" role="note">{t`You are signed in as ${signedInAs}. Sign out, then sign in as ${q.data!.email} and open the link from the e-mail again.`}</p>
                  <Button type="button" variant="secondary" className="w-full" onClick={() => void logout()}>{t`Sign out`}</Button>
                </>
              ) : (
                <>
                  <p className="mb-4 text-sm" role="note">{t`You already have a ViewStream account. Sign in as ${q.data!.email} to accept the invitation.`}</p>
                  <Button type="button" className="w-full" onClick={() => void navigate({ to: '/login', search: { next: `/invite/${token}` } })}>{t`Sign in to accept`}</Button>
                </>
              )
            ) : q.data!.existing_user ? (
              <>
                {err ? <p role="alert" className="mb-2 text-sm text-danger">{err}</p> : null}
                <Button type="submit" className="w-full" loading={busy}>{t`Accept invitation`}</Button>
              </>
            ) : (
              <>
                <Field id="name" label={t`Your name`}><Input value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" /></Field>
                <Field id="password" label={t`Choose a password`} hint={t`At least 12 characters.`} error={err}><Input type="password" minLength={12} autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} dir="ltr" /></Field>
                <Button type="submit" className="w-full" loading={busy}>{t`Set password and sign in`}</Button>
              </>
            )}
          </form>
        )}
      </div>
    </main>
  );
}
